import React, { useState, useEffect } from 'react';
import { Save, ArrowLeft, Upload, Eye, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { blogService } from '../../services/blogService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function BlogPostEditor() {
  const [searchParams] = useSearchParams();
  const postId = searchParams?.get('id');
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    imageUrl: '',
    isPublished: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [settingsCollapsed, setSettingsCollapsed] = useState(false);

  useEffect(() => {
    if (postId) {
      loadPost();
    }
  }, [postId]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const post = await blogService?.getPostById(postId);
      setFormData({
        title: post?.title,
        slug: post?.slug,
        content: post?.content,
        imageUrl: post?.imageUrl || '',
        isPublished: post?.isPublished
      });
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTitleChange = (e) => {
    const title = e?.target?.value;
    setFormData({
      ...formData,
      title,
      slug: blogService?.generateSlug(title)
    });
  };

  const handleImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const imageUrl = await blogService?.uploadImage(file);
      setFormData({ ...formData, imageUrl });
      setSuccess('Görsel başarıyla yüklendi!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (!formData?.title?.trim()) {
      setError('Başlık gereklidir');
      return;
    }

    if (!formData?.content?.trim()) {
      setError('İçerik gereklidir');
      return;
    }

    try {
      setLoading(true);
      if (postId) {
        await blogService?.updatePost(postId, formData);
        setSuccess('Yazı başarıyla güncellendi!');
      } else {
        await blogService?.createPost(formData);
        setSuccess('Yazı başarıyla oluşturuldu!');
      }
      setTimeout(() => {
        navigate('/blog-management-dashboard');
      }, 1500);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image', 'video'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'code-block',
    'list',
    'bullet',
    'link',
    'image',
    'video'
  ];

  if (loading && postId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/blog-management-dashboard')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Geri Dön
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-blue-600" />
                <h1 className="text-xl font-bold text-gray-900">
                  {postId ? 'Yazıyı Düzenle' : 'Yeni Yazı Oluştur'}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showPreview
                    ? 'bg-blue-100 text-blue-700' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Eye className="w-4 h-4" />
                {showPreview ? 'Düzenlemeye Dön' : 'Önizleme'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Kaydediliyor...' : postId ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto px-6 mt-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-6 mt-4">
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Main Content Area - Full Width */}
          <div className="flex-1">
            {!showPreview ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Title Input */}
                <div className="p-6 border-b border-gray-200">
                  <input
                    type="text"
                    value={formData?.title}
                    onChange={handleTitleChange}
                    placeholder="Yazı başlığını girin..."
                    className="w-full text-3xl font-bold text-gray-900 placeholder-gray-400 focus:outline-none"
                  />
                </div>

                {/* Content Editor */}
                <div className="p-6">
                  <ReactQuill
                    theme="snow"
                    value={formData?.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-white"
                    style={{ height: '500px' }}
                    placeholder="İçeriğinizi buraya yazın..."
                  />
                </div>

                <div className="px-6 pb-6 mt-16">
                  <p className="text-xs text-gray-500">
                    Profesyonel metin editörü: Başlıklar, listeler, renkler, bağlantılar ve görseller ekleyebilirsiniz
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                {formData?.imageUrl && (
                  <img
                    src={formData?.imageUrl}
                    alt="Kapak"
                    className="w-full h-96 object-cover rounded-lg mb-8"
                  />
                )}
                <h1 className="text-4xl font-bold text-gray-900 mb-6">
                  {formData?.title || 'Yazı Başlığınız'}
                </h1>
                <div className="prose prose-lg max-w-none text-gray-700">
                  {formData?.content ? (
                    <div dangerouslySetInnerHTML={{ __html: formData?.content }} />
                  ) : (
                    <p className="text-gray-400 italic">İçeriğiniz burada görünecek...</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Settings Sidebar */}
          <div className="w-80">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-24">
              <button
                onClick={() => setSettingsCollapsed(!settingsCollapsed)}
                className="w-full flex items-center justify-between p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Yazı Ayarları</h3>
                {settingsCollapsed ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                )}
              </button>

              {!settingsCollapsed && (
                <div className="p-4 space-y-4">
                  {/* Slug */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL Slug
                    </label>
                    <input
                      type="text"
                      value={formData?.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e?.target?.value })}
                      placeholder="url-dostu-slug"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      /blog/{formData?.slug || 'slug'}
                    </p>
                  </div>

                  {/* Cover Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kapak Görseli
                    </label>
                    <input
                      type="text"
                      value={formData?.imageUrl}
                      onChange={(e) => setFormData({ ...formData, imageUrl: e?.target?.value })}
                      placeholder="Görsel URL'si"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm mb-2"
                    />
                    <label className="block cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      <div className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm">
                        <Upload className="w-4 h-4" />
                        {uploadingImage ? 'Yükleniyor...' : 'Görsel Yükle'}
                      </div>
                    </label>
                    {formData?.imageUrl && (
                      <img
                        src={formData?.imageUrl}
                        alt="Önizleme"
                        className="mt-3 w-full h-32 object-cover rounded-lg"
                      />
                    )}
                  </div>

                  {/* Kategori (placeholder for future) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kategori
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm">
                      <option>Genel</option>
                      <option>Teknik</option>
                      <option>Etkinlikler</option>
                      <option>Topluluk</option>
                    </select>
                  </div>

                  {/* Published Status */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="isPublished"
                        checked={formData?.isPublished}
                        onChange={(e) => setFormData({ ...formData, isPublished: e?.target?.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <label htmlFor="isPublished" className="text-sm font-medium text-gray-700">
                        Hemen yayınla
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      İşaretli değilse taslak olarak kaydedilir
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}